import React from "react";

import { addDoc, collection } from "firebase/firestore";
import { DocumentData, WithFieldValue } from "firebase/firestore";
import { Alert, ButtonProps, Text } from "@chakra-ui/react";
import { DB_COLLECTION_NAMES, PRODUCTION_STEPS_AUX } from "@constants";
import { FirebaseContext } from "@contexts";
import { useAuth, useCustomToast } from "@hooks";
import { getCountFromServer, query, orderBy, where } from "firebase/firestore";
import { ButtonModal, JsonForm, JsonFormControl } from "@components";

type AddCSVModalProps = ButtonProps & {
  itemId: string;
};

// Want to parse text that might have commas within quotation marks
function parse(row: string) {
  let insideQuote = false;
  const entries = [];
  let entry: string[] = [];

  row.split("").forEach(function (character) {
    if (character === '"') {
      insideQuote = !insideQuote;
    } else {
      if (character == "," && !insideQuote) {
        entries.push(entry.join(""));
        entry = [];
      } else {
        entry.push(character);
      }
    }
  });
  entries.push(entry.join(""));
  return entries;
}

const readUploadedFileAsText = (inputFile: Blob) => {
  const temporaryFileReader = new FileReader();

  return new Promise<string>((resolve, reject) => {
    temporaryFileReader.onerror = () => {
      temporaryFileReader.abort();
      reject(new DOMException("Problem parsing input file."));
    };

    temporaryFileReader.onload = () => {
      resolve(temporaryFileReader.result as string);
    };
    temporaryFileReader.readAsText(inputFile);
  });
};

const AddCSVFormFields: JsonFormControl[] = [
  {
    label: "CSVInput",
    inputType: "csv",
    fieldName: "csv",
    defaultValue: undefined,
  },
];

export function AddCSVModal({ itemId, ...delegated }: AddCSVModalProps) {
  const authData = useAuth();

  const [error, setError] = React.useState("");
  // The number of documents in the total query.
  const { firestore } = React.useContext(FirebaseContext);
  const collectionName = DB_COLLECTION_NAMES.STEPS;

  // Beginning of comment for tag.
  // This allows us to search for this text to avoid writing duplicate tags.
  const comment = "Imported data from CERN DB: ";

  const tagAddedToast = useCustomToast(() => (
    <Alert status="success">Data from CSV added!</Alert>
  ));

  const tagFailedToast = useCustomToast(() => (
    <Alert status="success">Data from CSV failed!</Alert>
  ));

  const serverFailedToast = useCustomToast(() => (
    <Alert status="success">Unable to get DB info!</Alert>
  ));

  const tagExistsToast = useCustomToast(() => (
    <Alert status="success">Data already exists!</Alert>
  ));

  const queryProblemToast = useCustomToast(() => (
    <Alert status="success">Undefined query</Alert>
  ));

  const doSubmit = React.useCallback(
    async function <T extends WithFieldValue<DocumentData>>(data: T) {
      const userId = authData.user?.firebaseUser?.uid || "";
      const userName =
        authData.user?.firstName && authData.user?.lastName
          ? `${authData.user.firstName} ${authData.user.lastName}`
          : "";

      const csv = data.csv;

      const fileAsText = readUploadedFileAsText(csv);
      const allLines = (await fileAsText).split(/\r\n|\n/);

      for await (const line of allLines) {
        if (!line) continue;
        const columns = parse(line);
        const moduleId = columns[0];
        const moduleLocation = columns[1];
        const moduleTag = columns[2];
        const moduleComment = columns[3];

        const collectionRef = collection(firestore, collectionName);

        // Find the number of steps from this module that contain the right text
        // that indicates that this has already been done.
        const queryResult = query(
          collectionRef,
          orderBy("isCernImport"),
          orderBy("moduleId"),
          where("isCernImport", "==", 1),
          where("moduleId", "==", moduleId),
        );

        let count = -1;
        try {
          const snapshot = await getCountFromServer(queryResult);
          if (snapshot == undefined) {
            queryProblemToast();
          }
          count = (await snapshot).data().count;
        } catch (ex) {
          alert(ex);
          console.error("CSV upload failed, problem with query", moduleId);
        }

        if (count == -1) {
          serverFailedToast();
        } else if (count == 0) {
          setError("");
          if (moduleTag == "Bad" || moduleTag == "Damaged") {
            const tag = "CERN DB flags as bad";
            const fullComment = comment + moduleComment;

            const step = {
              stepName: PRODUCTION_STEPS_AUX.STEP_AUX_TAG,
              userId: userId,
              userName: userName,
              moduleId: moduleId,
              location: moduleLocation,
              mainComponentSerial: moduleId,
              details: { tag },
              timestamp: new Date(),
              comments: fullComment,
              isCernImport: 1,
            };
            try {
              await addDoc(collection(firestore, collectionName), step);
              tagAddedToast();
            } catch (ex) {
              alert(ex);
              tagFailedToast();
              console.error("CSV upload failed", ex);
            }
          } else if (moduleComment != "") {
            //const tag = "Imported data from CERN";
            const fullComment = comment + moduleComment;

            const step = {
              stepName: PRODUCTION_STEPS_AUX.STEP_AUX_TAG,
              userId: userId,
              userName: userName,
              moduleId: moduleId,
              location: moduleLocation,
              mainComponentSerial: moduleId,
              details: {},
              timestamp: new Date(),
              comments: fullComment,
              isCernImport: 1,
            };
            try {
              await addDoc(collection(firestore, collectionName), step);
              tagAddedToast();
            } catch (ex) {
              alert(ex);
              tagFailedToast();
              console.error("CSV upload failed", ex);
            }
          }
        } else {
          tagExistsToast();
        }
      }
    },
    [itemId],
  );

  return (
    <ButtonModal label="Import CSV" title="Import CSV" size="sm" {...delegated}>
      <JsonForm
        fields={AddCSVFormFields}
        onSubmit={doSubmit}
        clearAfterSubmit
      />
      {error ? <Text>{error}</Text> : undefined}
    </ButtonModal>
  );
}
